import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";

const URL = "https://palstars.com/prices/legends-awaken";
const TITLE = "Legends Awaken TCG Card Price Guide & Chart";
const DESC =
  "Legends Awaken (EBP02) Palworld TCG card price guide: how launch-window prices behave, how Pal Stars tracks eBay and TCGplayer, and a pre-release checklist.";
const KEY = "palstars:ebp02-watch";
const CODES = Array.from({ length: 20 }, (_, i) => `EBP02-${String(i + 1).padStart(3, "0")}`);

const FAQ: FaqItem[] = [
  {
    q: "When will Legends Awaken card prices appear?",
    a: "Tracking starts with the first daily snapshot after the October 30, 2026 release, once Bushiroad publishes the official English card codes.",
  },
  {
    q: "Where do Legends Awaken prices come from?",
    a: "Pal Stars pulls current eBay and TCGplayer listings twice a day, takes the median and drops extreme outliers so one mispriced listing can't move the chart.",
  },
];

export const Route = createFileRoute("/prices_/legends-awaken")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow, max-snippet:-1" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Prices", "/prices"], ["Legends Awaken", "/prices/legends-awaken"]]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: LegendsAwakenPrices,
});

function LegendsAwakenPrices() {
  const [watch, setWatch] = useState<string[]>([]);
  useEffect(() => {
    try {
      setWatch(JSON.parse(localStorage.getItem(KEY) ?? "[]"));
    } catch {
      /* ignore */
    }
  }, []);
  const toggle = (code: string) => {
    const next = watch.includes(code) ? watch.filter((c) => c !== code) : [...watch, code];
    setWatch(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-8">
      <header className="glass rounded-2xl p-6">
        <p className="text-xs uppercase tracking-widest text-primary">Booster Pack Legends Awaken · EBP02</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{TITLE}</h1>
        <p className="mt-3 inline-block rounded-md border border-primary/50 px-2 py-1 font-mono text-xs text-primary">
          [DATABASE SYNCHRONIZATION PENDING LAUNCH DAY SNAPSHOT]
        </p>
      </header>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Legends Awaken TCG Card Price Guide &amp; Set Analysis</h2>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground leading-relaxed">
          New sets rarely hold their opening-week prices. Early listings are thin, sellers price on hype, and values
          settle once boxes get opened in volume. Dawn of Palpagos showed both sides of that: plenty of Commons slid to
          well under a dollar, while the top chase cards like SSS Ultra Secret Gold printings kept four-figure asking
          prices. You can follow that pattern across every current card on the{" "}
          <a href="/tcg-price-index" className="text-primary hover:underline">Palworld TCG price index market trends</a> page.
        </p>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          For Legends Awaken, our tracker collects live eBay and TCGplayer listings twice a day, takes the median for
          each card and throws out extreme outliers, so a single $9,999 joke listing or a mislabeled lot can't skew the
          number. Nothing is estimated or back-filled; a card gets a price only after real listings exist.
        </p>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Predictive Asset Checklist Structure</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Mark the numbers you want to follow. Your watch list is saved on this device.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2 pr-3">Card number</th>
                <th className="py-2 pr-3">Card data</th>
                <th className="py-2">Tracker</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {CODES.map((c) => (
                <tr key={c}>
                  <td className="py-2 pr-3 font-mono">{c}</td>
                  <td className="py-2 pr-3 text-muted-foreground">[Card data pending official Bushiroad English code release]</td>
                  <td className="py-2">
                    <button
                      onClick={() => toggle(c)}
                      className="rounded-lg border border-primary/60 px-3 py-1 text-xs font-semibold hover:bg-primary/10"
                    >
                      {watch.includes(c) ? "Tracking" : "Set Live Price Tracker Alert"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          Buying sealed instead of singles? Compare box and case listings on the{" "}
          <a href="/marketplace" className="text-primary hover:underline">Palworld TCG bulk orders platform</a>.
        </p>
      </section>

      <FaqSection items={FAQ} heading="Legends Awaken price FAQ" />
    </div>
  );
}
